import { Col, Descriptions, Row, Typography } from "antd";
import dayjs from "dayjs";
import React from "react";
import { useSelector } from "react-redux";
import { dateFormat } from "../shared/formatting";
import LabelWithEditAction from "../shared/form/LabelWithEditAction";
import { BulkUpdateActionType } from "./bulkUpdate/BulkUpdateModalConfig";

const formatProductDisplay = (code, description) => {
  if (!description) return code || "";

  return code && code.trim() ? `${code} - ${description}` : description;
};

const getExpiryStatus = (expiryDate) => {
  if (!expiryDate) {
    return { text: "N/A", color: "inherit" };
  }

  const today = dayjs();
  const expiry = dayjs(expiryDate);
  const diffDays = expiry.diff(today, "day");

  if (diffDays >= 7) {
    return {
      text: `${diffDays} days to expiry`,
      color: "green",
    };
  }

  if (diffDays >= 1 && diffDays <= 6) {
    return {
      text: `${diffDays} day${diffDays === 1 ? "" : "s"} to expiry`,
      color: "orange",
    };
  }

  const pastDays = Math.abs(diffDays) || 0;
  return {
    text: `${pastDays} day${pastDays === 1 ? "" : "s"} past expiry`,
    color: "red",
  };
};

const RenewalSummary = ({ selected, onEditAction }) => {
  const maskSensitiveData = useSelector(
    (state) => state.actor?.details?.data?.MaskSensitiveData
  );

  const handleDocumentOpen = (documentKey) => {
    window.open(
      `${process.env.REACT_APP_BASENAME}documents/updatedocumentform/${documentKey}`,
      `${documentKey}`
    );
  };

  return (
    <Row>
      <Col span={24}>
        {selected && (
          <>
            <Descriptions
              size="small"
              column={3}
              items={[
                {
                  key: "customer",
                  label: "Customer",
                  children: (
                    <div
                      className={
                        maskSensitiveData === true ? "blurry-text" : ""
                      }
                    >
                      {selected.InsuredName}
                    </div>
                  ),
                },
                {
                  key: "product",
                  label: "Product",
                  children: formatProductDisplay(
                    selected.ProductCode,
                    selected.ProductDescription,
                  ),
                },

                {
                  key: "assignedTo",
                  label: (
                    <LabelWithEditAction
                      text="Assigned To"
                      onEdit={() => onEditAction(BulkUpdateActionType.ASSIGNEE)}
                    />
                  ),
                  labelStyle: { overflow: "visible" },
                  children: selected.AssignedTo || "(Unassigned)",
                },
                {
                  key: "agency",
                  label: "Agency Of Record",
                  children: selected.AgencyNumberAndName,
                },

                {
                  key: "policyStatusChangeAgency",
                  label: "Processing Agency",
                  children: selected.PolicyStatusChangeAgency,
                },
                {
                  key: "processingStage",
                  label: "Processing Stage",
                  children: selected.LatestProcessingStageDesc,
                },
                {
                  key: "vehicle",
                  label: "Vehicle",
                  children: selected.Vehicle,
                },
                {
                  key: "expiry",
                  label: "Expiry",
                  children: selected.PolicyExpiryDate ? (
                    <span>
                      {dayjs(selected.PolicyExpiryDate).format(dateFormat[0])}{" "}
                      <span
                        style={{
                          color: getExpiryStatus(selected.PolicyExpiryDate).color,
                          fontWeight: 600,
                        }}
                      >
                        ({getExpiryStatus(selected.PolicyExpiryDate).text})
                      </span>
                    </span>
                  ) : (
                    "N/A"
                  ),
                },
                {
                  key: "reminderStatus",
                  label: "Processing Status",
                  children: selected.ProcessingStatusDesc,
                },
                {
                  key: "policyNumber",
                  label: "Policy",
                  children: selected.PolicyNumber,
                },

                {
                  key: "policyStatus",
                  label: "Policy Status",
                  children: selected.PolicyStatusDesc,
                },
                {
                  key: "dateModified",
                  label: "Last Updated On",
                  children: selected.DateModified
                    ? dayjs(selected.DateModified).format(`${dateFormat[0]} h:mm A`)
                    : "N/A",
                },
              ]}
            />
          </>
        )}
      </Col>
    </Row>
  );
};

export default RenewalSummary;
